import { useEffect, useState } from 'react';
import { apiFetch, ApiError } from '../../lib/apiClient';

/**
 * Top-3 incidents-as-kill-chain-stories.
 *
 * Lazy-loaded after the parent report has rendered. Calls
 * GET /api/kill-chain-stories which fetches incident metadata + correlated
 * alerts + an AI-generated 2-paragraph narrative per incident.
 *
 * Renders three "story cards" - each with a severity-coded header, a
 * vertical alert timeline, the AI narrative, and a footer of dwell / MTTR /
 * alert-count chips.
 *
 * Failure modes (each handled explicitly):
 *  - 401 (auth)            → render nothing (auth context will redirect)
 *  - 503 / not_configured  → hide the whole section silently
 *  - 502 / upstream KQL    → render an inline error banner
 *  - LLM failure per story → render the timeline + an "AI narrative
 *                            unavailable" inline notice, never blank
 *  - empty period          → friendly empty-state card
 */

export interface AlertEvent {
  timeUtc: string;
  timeLocal: string;
  alertName: string;
  productName: string;
  severity: string;
  tactics: string[];
  techniques: string[];
  entities: string[];
}

export interface KeyMoment {
  timeLocal: string;
  action: string;
}

export interface KillChainStory {
  incidentNumber: number;
  title: string;
  severity: string;
  status: string;
  classification: string;
  owner: string;
  createdUtc: string;
  closedUtc: string | null;
  createdLocal: string;
  closedLocal: string | null;
  dwellMinutes: number | null;
  mttrMinutes: number | null;
  alertCount: number;
  uniqueTactics: string[];
  events: AlertEvent[];
  narrative: string;
  keyMoments: KeyMoment[];
  narrativeError: string | null;
}

interface ApiResponse {
  customerId: string;
  period: 'weekly' | 'monthly';
  timezone: string;
  stories: KillChainStory[];
  errors: { incidents: boolean; alerts: boolean };
  generatedAt: string;
}

type Period = 'weekly' | 'monthly';

interface Props {
  customerId: string;
  period: Period;
  /** YYYY-MM-DD when period === 'weekly'; ignored for monthly. */
  weekEnding?: string;
  /** YYYY-MM when period === 'monthly'; ignored for weekly. */
  month?: string;
  /** Bump this to force a refetch (e.g. when customer or window changes). */
  cacheKey?: string;
}

// ── Color theme per severity ───────────────────────────────────────────────

const SEVERITY: Record<string, { bg: string; border: string; pillBg: string; pillFg: string; accent: string }> = {
  high: {
    bg: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
    border: '#fca5a5',
    pillBg: '#dc2626',
    pillFg: '#ffffff',
    accent: '#dc2626',
  },
  medium: {
    bg: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
    border: '#fcd34d',
    pillBg: '#d97706',
    pillFg: '#ffffff',
    accent: '#d97706',
  },
  low: {
    bg: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
    border: '#93c5fd',
    pillBg: '#2563eb',
    pillFg: '#ffffff',
    accent: '#2563eb',
  },
  informational: {
    bg: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
    border: '#cbd5e1',
    pillBg: '#64748b',
    pillFg: '#ffffff',
    accent: '#64748b',
  },
};

function themeFor(severity: string) {
  return SEVERITY[severity.toLowerCase()] ?? SEVERITY['informational']!;
}

function fmtDuration(min: number | null): string {
  if (min === null || !Number.isFinite(min)) return ' - ';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h < 24) return m === 0 ? `${h}h` : `${h}h ${m}m`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh === 0 ? `${d}d` : `${d}d ${rh}h`;
}

// ── Component ──────────────────────────────────────────────────────────────

export function KillChainStories({ customerId, period, weekEnding, month, cacheKey }: Props) {
  const [state, setState] = useState<
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'ready'; data: ApiResponse }
    | { kind: 'hidden' }
    | { kind: 'unavailable'; reason: string }
  >({ kind: 'idle' });

  useEffect(() => {
    if (!customerId) { setState({ kind: 'idle' }); return; }
    if (period === 'weekly' && !weekEnding) { setState({ kind: 'idle' }); return; }
    if (period === 'monthly' && !month) { setState({ kind: 'idle' }); return; }

    const ctrl = new AbortController();
    let cancelled = false;
    setState({ kind: 'loading' });

    const params = new URLSearchParams({ customerId, period });
    if (period === 'weekly' && weekEnding) params.set('weekEnding', weekEnding);
    if (period === 'monthly' && month) params.set('month', month);

    apiFetch<ApiResponse>(`/kill-chain-stories?${params.toString()}`, { signal: ctrl.signal })
      .then((r) => { if (!cancelled) setState({ kind: 'ready', data: r }); })
      .catch((e: ApiError | Error) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.notAuthenticated) { setState({ kind: 'idle' }); return; }
        // Hide silently when the AI is simply not configured - the rest of
        // the report below stands on its own.
        if (e instanceof ApiError && (e.body['error'] === 'not_configured' || e.circuitOpen)) {
          setState({ kind: 'hidden' });
          return;
        }
        const reason = e instanceof Error ? e.message : String(e);
        setState({ kind: 'unavailable', reason });
      });

    return () => { cancelled = true; ctrl.abort(); };
  }, [customerId, period, weekEnding, month, cacheKey]);

  if (state.kind === 'idle' || state.kind === 'hidden') return null;

  if (state.kind === 'loading') {
    return (
      <div
        style={{
          background: '#f8fafc',
          border: '1px dashed #cbd5e1',
          borderRadius: 12,
          padding: '20px 24px',
          color: '#475569',
          fontSize: '0.9em',
        }}
        aria-busy="true"
        aria-live="polite"
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <Spinner />
          <strong style={{ color: '#0f172a' }}>Reconstructing the {period === 'weekly' ? "week's" : "month's"} top kill-chains…</strong>
        </div>
        <div style={{ fontSize: '0.85em' }}>
          Pulling the highest-impact incidents and asking the AI to narrate each one. Usually 10–25 seconds.
        </div>
      </div>
    );
  }

  if (state.kind === 'unavailable') {
    return (
      <div
        style={{
          background: '#fff7ed',
          border: '1px solid #fed7aa',
          borderRadius: 12,
          padding: '12px 18px',
          color: '#9a3412',
          fontSize: '0.85em',
        }}
      >
        <strong>Kill-chain stories unavailable.</strong>{' '}
        <span style={{ color: '#7c2d12' }}>{state.reason}</span>{' '}
        The full report below is unaffected.
      </div>
    );
  }

  const { stories, errors } = state.data;

  if (stories.length === 0) {
    return (
      <div
        className="kcs-empty"
        style={{
          background: '#f0fdf4',
          border: '1px solid #86efac',
          borderRadius: 12,
          padding: '16px 20px',
          color: '#166534',
          fontSize: '0.9em',
        }}
      >
        <strong>No high-impact kill-chains this {period === 'weekly' ? 'week' : 'month'}.</strong>{' '}
        {errors.incidents
          ? "We couldn't pull incidents from the workspace, so this is a best-effort empty state - please check the partial-data banner above."
          : 'Either no incidents crossed the severity threshold, or every incident in the period was classified as a benign positive.'}
      </div>
    );
  }

  return (
    <div className="kcs-root">
      <ScopedStyles />
      {stories.map((s) => <StoryCard key={s.incidentNumber} story={s} />)}
    </div>
  );
}

function StoryCard({ story }: { story: KillChainStory }) {
  const t = themeFor(story.severity);
  const paragraphs = (story.narrative ?? '').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  return (
    <div className="kcs-card" style={{ background: t.bg, borderColor: t.border }}>
      {/* ── Header ───────────────────────────────────────────────── */}
      <div className="kcs-card-header">
        <div className="kcs-header-left">
          <span className="kcs-pill" style={{ background: t.pillBg, color: t.pillFg }}>
            {story.severity || 'UNKNOWN'}
          </span>
          <span className="kcs-incident-num">#{story.incidentNumber}</span>
          <h3 className="kcs-title">{story.title}</h3>
        </div>
        <div className="kcs-header-right">
          <StatusChip status={story.status} />
          {story.classification && <ClassificationChip classification={story.classification} />}
        </div>
      </div>

      {/* ── Footer stats (dwell / MTTR / alerts) - placed early for impact ── */}
      <div className="kcs-stats">
        <Stat label="Alerts" value={String(story.alertCount)} accent={t.accent} />
        <Stat label="Dwell" value={fmtDuration(story.dwellMinutes)} accent={t.accent}
              hint="From first signal to containment" />
        <Stat label="MTTR" value={fmtDuration(story.mttrMinutes)} accent={t.accent}
              hint="Mean time to resolve (creation → closure)" />
        <Stat label="Tactics" value={String(story.uniqueTactics.length)} accent={t.accent}
              hint={story.uniqueTactics.join(', ') || undefined} />
      </div>

      {/* ── AI narrative ─────────────────────────────────────────── */}
      {story.narrative ? (
        <div className="kcs-narrative">
          <div className="kcs-narrative-header">
            <SparkleIcon /> <span>AI-narrated walkthrough</span>
          </div>
          {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
        </div>
      ) : story.narrativeError ? (
        <div className="kcs-narrative-error">
          <strong>AI narrative unavailable.</strong> {story.narrativeError} Timeline below is unaffected.
        </div>
      ) : null}

      {/* ── Timeline ─────────────────────────────────────────────── */}
      <div className="kcs-timeline-wrap">
        <div className="kcs-timeline-header">
          Kill-chain timeline
          <span className="kcs-timeline-meta">{story.events.length} alert{story.events.length === 1 ? '' : 's'}</span>
        </div>
        {story.events.length === 0 ? (
          <div className="kcs-timeline-empty">
            No correlated alerts could be retrieved for this incident - the alert table may not be available, or the incident references alerts outside the report window.
          </div>
        ) : (
          <ol className="kcs-timeline">
            <TimelineItem
              timeLocal={story.createdLocal}
              title="Incident created"
              accent={t.accent}
              isMilestone
            />
            {story.events.map((e, i) => (
              <TimelineItem
                key={`${e.timeUtc}-${i}`}
                timeLocal={e.timeLocal || ' - '}
                title={e.alertName}
                subtitle={e.productName}
                tags={e.tactics}
                entities={e.entities}
                severity={e.severity}
                accent={t.accent}
                isKeyMoment={story.keyMoments.some((m) => m.timeLocal === e.timeLocal)}
              />
            ))}
            {story.closedLocal && (
              <TimelineItem
                timeLocal={story.closedLocal}
                title={`Incident ${story.status?.toLowerCase() === 'closed' ? 'closed' : 'resolved'}`}
                accent={t.accent}
                isMilestone
              />
            )}
          </ol>
        )}
      </div>

      {/* ── Key moments callout (AI summary of the chain) ───────── */}
      {story.keyMoments.length > 0 && (
        <div className="kcs-key-moments">
          <div className="kcs-key-moments-header">Key moments</div>
          <ul>
            {story.keyMoments.map((m, i) => (
              <li key={i}>
                <span className="kcs-km-time">{m.timeLocal}</span>
                <span className="kcs-km-action">{m.action}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function StatusChip({ status }: { status: string }) {
  const norm = (status || '').toLowerCase();
  const bg = norm === 'closed' ? '#dcfce7' : norm === 'active' ? '#fef9c3' : '#e2e8f0';
  const fg = norm === 'closed' ? '#166534' : norm === 'active' ? '#854d0e' : '#475569';
  return (
    <span style={{ background: bg, color: fg, padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600 }}>
      {status || 'Unknown'}
    </span>
  );
}

function ClassificationChip({ classification }: { classification: string }) {
  const isBenign = /benign/i.test(classification);
  const isTP = /truepositive/i.test(classification);
  const bg = isBenign ? '#e0f2fe' : isTP ? '#fee2e2' : '#f1f5f9';
  const fg = isBenign ? '#075985' : isTP ? '#991b1b' : '#475569';
  return (
    <span style={{ background: bg, color: fg, padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600 }}>
      {classification.replace(/([a-z])([A-Z])/g, '$1 $2')}
    </span>
  );
}

function Stat({ label, value, accent, hint }: { label: string; value: string; accent: string; hint?: string }) {
  return (
    <div className="kcs-stat" title={hint}>
      <div className="kcs-stat-label">{label}</div>
      <div className="kcs-stat-value" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function TimelineItem({
  timeLocal, title, subtitle, tags, entities, severity, accent, isKeyMoment, isMilestone,
}: {
  timeLocal: string;
  title: string;
  subtitle?: string;
  tags?: string[];
  entities?: string[];
  severity?: string;
  accent: string;
  isKeyMoment?: boolean;
  isMilestone?: boolean;
}) {
  return (
    <li className={`kcs-tl-item ${isMilestone ? 'kcs-tl-milestone' : ''} ${isKeyMoment ? 'kcs-tl-key' : ''}`}>
      <div className="kcs-tl-time">{timeLocal}</div>
      <div className="kcs-tl-marker" style={{ background: isMilestone ? accent : '#fff', borderColor: accent }} />
      <div className="kcs-tl-body">
        <div className="kcs-tl-title">
          {title}
          {severity && <span className={`kcs-tl-sev kcs-tl-sev-${severity.toLowerCase()}`}>{severity}</span>}
        </div>
        {subtitle && <div className="kcs-tl-subtitle">{subtitle}</div>}
        {(tags && tags.length > 0) && (
          <div className="kcs-tl-tags">
            {tags.slice(0, 4).map((tag) => (
              <span key={tag} className="kcs-tl-tag">{tag}</span>
            ))}
          </div>
        )}
        {(entities && entities.length > 0) && (
          <div className="kcs-tl-entities">
            <span className="kcs-tl-entities-label">Entities:</span>{' '}
            {entities.slice(0, 3).map((e, i) => (
              <span key={i} className="kcs-tl-entity">{e}</span>
            ))}
          </div>
        )}
      </div>
    </li>
  );
}

function Spinner() {
  return (
    <span
      style={{
        display: 'inline-block',
        width: 14,
        height: 14,
        border: '2px solid #cbd5e1',
        borderTopColor: '#0f172a',
        borderRadius: '50%',
        animation: 'kcs-spin 0.8s linear infinite',
      }}
    >
      <style>{`@keyframes kcs-spin { to { transform: rotate(360deg); } }`}</style>
    </span>
  );
}

function SparkleIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M8 1l1.5 4.5L14 7l-4.5 1.5L8 13l-1.5-4.5L2 7l4.5-1.5L8 1z" />
    </svg>
  );
}

// All visual styling is scoped under `.kcs-root` so it cannot bleed into
// other report sections (the Insider-Threat Spotlight uses `its-`, etc).
function ScopedStyles() {
  return (
    <style>{`
.kcs-root { display: flex; flex-direction: column; gap: 16px; }

.kcs-card {
  border: 1px solid;
  border-radius: 14px;
  padding: 18px 22px;
  box-shadow: 0 4px 12px rgba(15,23,42,0.05);
}

.kcs-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 12px;
}
.kcs-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  flex: 1 1 auto;
  min-width: 0;
}
.kcs-header-right { display: flex; gap: 6px; flex-wrap: wrap; }
.kcs-pill {
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 1px;
  padding: 3px 10px;
  border-radius: 999px;
  text-transform: uppercase;
  white-space: nowrap;
}
.kcs-incident-num {
  font-family: ui-monospace, monospace;
  color: #475569;
  font-size: 12px;
  font-weight: 600;
  background: rgba(255,255,255,0.6);
  padding: 2px 8px;
  border-radius: 6px;
  white-space: nowrap;
}
.kcs-title {
  margin: 0;
  font-size: 1.05em;
  color: #0f172a;
  font-weight: 700;
  line-height: 1.35;
  flex: 1 1 200px;
  min-width: 0;
}

.kcs-stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0,1fr));
  gap: 10px;
  margin: 4px 0 14px;
  padding: 10px 14px;
  background: rgba(255,255,255,0.55);
  border: 1px solid rgba(15,23,42,0.06);
  border-radius: 10px;
}
.kcs-stat { text-align: center; min-width: 0; }
.kcs-stat-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #64748b;
  font-weight: 700;
  margin-bottom: 2px;
}
.kcs-stat-value {
  font-size: 1.25em;
  font-weight: 800;
  font-family: ui-monospace, monospace;
}

.kcs-narrative {
  background: rgba(255,255,255,0.7);
  border: 1px solid rgba(15,23,42,0.06);
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 14px;
  color: #0f172a;
  font-size: 0.92em;
  line-height: 1.55;
}
.kcs-narrative-header {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6d28d9;
  margin-bottom: 8px;
}
.kcs-narrative p { margin: 0 0 8px; }
.kcs-narrative p:last-child { margin-bottom: 0; }

.kcs-narrative-error {
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 10px;
  padding: 8px 12px;
  margin-bottom: 14px;
  color: #9a3412;
  font-size: 12px;
}

.kcs-timeline-wrap {
  background: rgba(255,255,255,0.55);
  border: 1px solid rgba(15,23,42,0.06);
  border-radius: 10px;
  padding: 12px 16px 14px;
  margin-bottom: 12px;
}
.kcs-timeline-header {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #475569;
  font-weight: 700;
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.kcs-timeline-meta { font-size: 11px; color: #64748b; font-weight: 500; text-transform: none; letter-spacing: 0; }
.kcs-timeline-empty { font-size: 12px; color: #64748b; padding: 6px 0; }

.kcs-timeline {
  list-style: none;
  margin: 0;
  padding: 0;
  position: relative;
}
.kcs-timeline::before {
  content: '';
  position: absolute;
  left: 64px;
  top: 6px;
  bottom: 6px;
  width: 2px;
  background: #e2e8f0;
}

.kcs-tl-item {
  position: relative;
  display: grid;
  grid-template-columns: 60px 18px 1fr;
  align-items: flex-start;
  gap: 0;
  padding: 6px 0 8px;
}
.kcs-tl-time {
  font-family: ui-monospace, monospace;
  font-size: 12px;
  font-weight: 700;
  color: #334155;
  padding-right: 8px;
  text-align: right;
  line-height: 18px;
}
.kcs-tl-marker {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid;
  margin: 4px 0 0 2px;
  z-index: 1;
  flex-shrink: 0;
}
.kcs-tl-milestone .kcs-tl-marker { width: 14px; height: 14px; margin-top: 3px; margin-left: 1px; }
.kcs-tl-key .kcs-tl-marker { box-shadow: 0 0 0 4px rgba(124,58,237,0.18); }
.kcs-tl-body {
  padding-left: 12px;
  font-size: 13px;
  line-height: 1.4;
  color: #0f172a;
  min-width: 0;
}
.kcs-tl-title { font-weight: 600; word-wrap: break-word; }
.kcs-tl-milestone .kcs-tl-title { color: #475569; font-style: italic; }
.kcs-tl-subtitle { font-size: 11px; color: #64748b; margin-top: 1px; }
.kcs-tl-sev {
  display: inline-block;
  margin-left: 6px;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 1px 6px;
  border-radius: 999px;
  vertical-align: middle;
}
.kcs-tl-sev-high { background: #fee2e2; color: #991b1b; }
.kcs-tl-sev-medium { background: #fef3c7; color: #854d0e; }
.kcs-tl-sev-low { background: #dbeafe; color: #1e40af; }
.kcs-tl-sev-informational { background: #f1f5f9; color: #475569; }

.kcs-tl-tags { margin-top: 4px; display: flex; gap: 4px; flex-wrap: wrap; }
.kcs-tl-tag {
  background: #ede9fe;
  color: #5b21b6;
  font-size: 10px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 4px;
  white-space: nowrap;
}
.kcs-tl-entities { margin-top: 4px; font-size: 11px; color: #475569; }
.kcs-tl-entities-label { font-weight: 600; color: #64748b; }
.kcs-tl-entity {
  font-family: ui-monospace, monospace;
  background: #f1f5f9;
  color: #1e293b;
  padding: 0 5px;
  border-radius: 3px;
  margin-right: 4px;
}

.kcs-key-moments {
  background: #faf5ff;
  border: 1px solid #e9d5ff;
  border-radius: 10px;
  padding: 10px 14px;
}
.kcs-key-moments-header {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #6d28d9;
  font-weight: 700;
  margin-bottom: 6px;
}
.kcs-key-moments ul {
  margin: 0;
  padding: 0;
  list-style: none;
}
.kcs-key-moments li {
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 3px 0;
  font-size: 13px;
  color: #1e1b4b;
}
.kcs-km-time {
  font-family: ui-monospace, monospace;
  font-weight: 700;
  font-size: 12px;
  color: #6d28d9;
  min-width: 48px;
}
.kcs-km-action { line-height: 1.4; }

@media (max-width: 720px) {
  .kcs-stats { grid-template-columns: repeat(2, minmax(0,1fr)); }
  .kcs-tl-item { grid-template-columns: 48px 16px 1fr; }
  .kcs-tl-time { font-size: 11px; }
  .kcs-timeline::before { left: 52px; }
}

@media print {
  .kcs-card { break-inside: avoid; box-shadow: none; }
  .kcs-narrative, .kcs-timeline-wrap, .kcs-key-moments { background: #fff; }
}
    `}</style>
  );
}
